import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import HomeSection from '../../../organisms/HomeEditorial/HomeSection';
import FriendshipUserCard from '../../../molecules/FriendshipUserCard';
import BandMemberInstrumentControl from './BandMemberInstrumentControl';
import { resolveUserDisplayName, resolveUserUsername } from '../../../../utils/userIdentity';

const BAND_OWNER_ROLE = 'OWNER';

const normalizeId = (value) => (value == null ? '' : String(value));

const isOwnerMembership = (member) => (
  String(member?.membershipRole || member?.role || '').trim().toUpperCase() === BAND_OWNER_ROLE
);

/**
 * Lineup grid: reuses the Friends page's FriendshipUserCard so the band-page
 * member cards stay visually and behaviourally identical to user cards
 * elsewhere in the app. `layout` is fixed to "grid" for now since the band
 * page does not expose a layout switch yet; switching to "list" later would
 * route to FriendListRow without any changes here.
 */
export default function BandMembersSection({
  id = 'members',
  eyebrow,
  title,
  side,
  members = [],
  onMemberClick,
  editing = false,
  currentUserId = null,
  isBandOwner = false,
  isCurrentUserMember = false,
  onSaveMemberInstrument,
  onRemoveMember,
  removingMemberUserId = null,
  publicMedia = false,
}) {
  const { t } = useTranslation();
  const normalizedCurrentUserId = normalizeId(currentUserId);
  const normalizedRemovingMemberUserId = normalizeId(removingMemberUserId);

  // Pending invites are private to the band: only the owner and confirmed
  // members may see who has been invited. Everyone else sees the lineup only.
  const canViewPendingMembers = isBandOwner || isCurrentUserMember;
  const visibleMembers = canViewPendingMembers
    ? members
    : members.filter((member) => !member.isPending);

  if (visibleMembers.length === 0) return null;

  return (
    <HomeSection
      id={id}
      eyebrow={eyebrow || t('bandPage.members.eyebrow')}
      title={title || t('bandPage.members.title')}
      side={side}
      variant="warm"
      className="band-members"
    >
      <div className="band-members__grid">
        {visibleMembers.map((member) => {
          const displayName = resolveUserDisplayName(member, '');
          const username = resolveUserUsername(member);
          const memberUserId = normalizeId(member.userId);
          const isPending = Boolean(member.isPending);
          const memberLabel = displayName || username || t('bandPage.members.memberFallback', { defaultValue: 'member' });
          const canEditInstrument = Boolean(
            !isPending
              && editing
              && typeof onSaveMemberInstrument === 'function'
              && member.userId
              && (
                isBandOwner
                || (normalizedCurrentUserId && memberUserId === normalizedCurrentUserId)
              ),
          );
          const isRemovingMember = Boolean(
            normalizedRemovingMemberUserId && memberUserId === normalizedRemovingMemberUserId,
          );
          const canRemoveMember = Boolean(
            !isPending
              && editing
              && isBandOwner
              && typeof onRemoveMember === 'function'
              && memberUserId
              && memberUserId !== normalizedCurrentUserId
              && !isOwnerMembership(member),
          );
          // Owners can revoke an outstanding invite at any time (like sending
          // one). It hits the same member-removal endpoint, so no edit-mode gate.
          const canRevokeInvite = Boolean(
            isPending
              && isBandOwner
              && typeof onRemoveMember === 'function'
              && memberUserId,
          );
          let actions = [];
          if (canRevokeInvite) {
            actions = [{
              // `cancel` key segment maps to the cross icon (see FriendshipActionIcons).
              key: 'cancel-invite',
              label: t('bandPage.members.revokeInvite', { defaultValue: 'Revoke invite' }),
              ariaLabel: t('bandPage.members.revokeInviteAria', {
                name: memberLabel,
                defaultValue: `Revoke invitation for ${memberLabel}`,
              }),
              variant: 'danger',
              disabled: isRemovingMember,
              busy: isRemovingMember,
              onClick: () => onRemoveMember(member),
            }];
          } else if (canRemoveMember) {
            actions = [{
              key: 'remove-member',
              label: t('bandPage.members.removeMember', { defaultValue: 'Remove' }),
              ariaLabel: t('bandPage.members.removeMemberAria', {
                name: memberLabel,
                defaultValue: `Remove ${memberLabel}`,
              }),
              variant: 'danger',
              disabled: isRemovingMember,
              busy: isRemovingMember,
              onClick: () => onRemoveMember(member),
            }];
          }
          return (
            <FriendshipUserCard
              key={member.id || member.userId}
              layout="grid"
              userId={member.userId}
              username={username || undefined}
              displayName={displayName || username || undefined}
              avatarUrl={member.avatarUrl}
              publicMedia={publicMedia}
              muted={isPending}
              badgeText={isPending
                ? t('bandPage.members.invitedBadge', { defaultValue: 'Invited' })
                : undefined}
              userInfoPrimaryText={isPending ? undefined : (member.role || undefined)}
              userInfoPrimaryContent={canEditInstrument ? (
                <BandMemberInstrumentControl
                  member={member}
                  onSaveInstrument={onSaveMemberInstrument}
                />
              ) : undefined}
              userInfoSecondaryText={member.badge || undefined}
              actions={actions}
              avatarAlt={displayName || undefined}
              previewAriaLabel={displayName ? t('bandPage.members.previewAria', { name: displayName, defaultValue: `View ${displayName}` }) : undefined}
              onPreview={onMemberClick ? () => onMemberClick(member) : undefined}
            />
          );
        })}
      </div>
    </HomeSection>
  );
}

BandMembersSection.propTypes = {
  id: PropTypes.string,
  eyebrow: PropTypes.node,
  title: PropTypes.node,
  side: PropTypes.node,
  members: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    userId: PropTypes.string.isRequired,
    username: PropTypes.string,
    displayName: PropTypes.string,
    avatarUrl: PropTypes.string,
    role: PropTypes.string,
    membershipRole: PropTypes.string,
    membershipStatus: PropTypes.string,
    mainInstrument: PropTypes.string,
    badge: PropTypes.string,
    isPending: PropTypes.bool,
  })),
  onMemberClick: PropTypes.func,
  editing: PropTypes.bool,
  currentUserId: PropTypes.string,
  isBandOwner: PropTypes.bool,
  isCurrentUserMember: PropTypes.bool,
  onSaveMemberInstrument: PropTypes.func,
  onRemoveMember: PropTypes.func,
  removingMemberUserId: PropTypes.string,
  publicMedia: PropTypes.bool,
};
